import { cn } from './cn.js'
import { useCountUp } from './useCountUp.js'

export interface RingProps {
  /** 0..1 of the daily budget consumed. */
  progress: number
  /** Large centred figure — calories still available, or the overage. */
  value: number
  label: string
  sublabel?: string
  over?: boolean
  size?: number
  className?: string
}

/**
 * The day's energy ring. Drawn as SVG rather than animated in JS so it stays
 * smooth while the camera or a list is doing real work on the main thread.
 */
export function Ring({
  progress,
  value,
  label,
  sublabel,
  over = false,
  size = 168,
  className,
}: RingProps) {
  const stroke = 10
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const clamped = Math.min(1, Math.max(0, progress))
  const dash = circumference * clamped
  // The centre figure counts to its new value as the ring sweeps to its new
  // arc — the one deliberate flourish on the money number.
  const shownValue = useCountUp(Math.abs(Math.round(value)))

  return (
    <div
      className={cn('relative grid place-items-center', className)}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
        className="-rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--g-line)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={over ? 'var(--g-danger)' : 'var(--g-accent)'}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`}
          style={{
            transition: 'stroke-dasharray 220ms var(--ease-out-quick), stroke 140ms linear',
          }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span
          className={cn(
            'tnum font-display text-3xl font-semibold leading-none',
            over ? 'text-danger' : 'text-ink',
          )}
        >
          {Math.round(shownValue)}
        </span>
        <span className="mt-1 text-sm text-dim">{label}</span>
        {sublabel != null && <span className="tnum text-xs text-mute">{sublabel}</span>}
      </div>
    </div>
  )
}

export interface MacroBarProps {
  label: string
  value: number
  target?: number | null
  unit?: string
  tone: 'protein' | 'carbs' | 'fat' | 'neutral'
  /**
   * `lg` stacks the label over the figure and sets the figure at reading size.
   * The three macros are the second thing looked at after the ring, and on one
   * cramped line they were the same weight as the four minor nutrients below.
   */
  size?: 'sm' | 'lg'
  className?: string
}

const TONE_VAR: Record<MacroBarProps['tone'], string> = {
  protein: 'var(--g-protein)',
  carbs: 'var(--g-carbs)',
  fat: 'var(--g-fat)',
  neutral: 'var(--g-dim)',
}

/**
 * A macro against its target. The bar is a 3px rule rather than a filled block:
 * at this density three stacked blocks would dominate the screen.
 */
export function MacroBar({
  label,
  value,
  target,
  unit = 'g',
  tone,
  size = 'sm',
  className,
}: MacroBarProps) {
  const pct = target != null && target > 0 ? Math.min(1, value / target) : 0
  const over = target != null && target > 0 && value > target
  const large = size === 'lg'

  const figure = (
    <>
      {Math.round(value)}
      {target != null && target > 0 && (
        <span className="text-mute">
          /{Math.round(target)}
          {unit}
        </span>
      )}
      {(target == null || target <= 0) && <span className="text-mute">{unit}</span>}
    </>
  )

  return (
    <div className={cn('flex flex-1 flex-col gap-1', className)}>
      {large ? (
        <>
          <span className="text-xs leading-tight text-mute">{label}</span>
          <span className="tnum text-lg leading-none text-ink">{figure}</span>
        </>
      ) : (
        <div className="flex items-baseline justify-between gap-1">
          <span className="text-xs text-mute">{label}</span>
          <span className="tnum text-sm text-ink">{figure}</span>
        </div>
      )}
      <div className={cn('w-full overflow-hidden rounded-full bg-line', large ? 'h-1' : 'h-[3px]')}>
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct * 100}%`,
            background: over ? 'var(--g-danger)' : TONE_VAR[tone],
            transition: 'width 200ms var(--ease-out-quick)',
          }}
        />
      </div>
    </div>
  )
}

export interface SparklineProps {
  points: readonly number[]
  target?: number | null
  height?: number
  className?: string
}

/** Trend line for the week. Deliberately unlabelled — the numbers live in the list. */
export function Sparkline({ points, target, height = 40, className }: SparklineProps) {
  if (points.length < 2) return <div style={{ height }} className={className} />

  const max = Math.max(...points, target ?? 0) || 1
  const step = 100 / (points.length - 1)
  const path = points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(2)},${(100 - (p / max) * 100).toFixed(2)}`,
    )
    .join(' ')
  const targetY = target != null && target > 0 ? 100 - (target / max) * 100 : null

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      height={height}
      className={cn('w-full', className)}
      aria-hidden="true"
    >
      {targetY != null && (
        <line
          x1="0"
          y1={targetY}
          x2="100"
          y2={targetY}
          stroke="var(--g-line-strong)"
          strokeWidth="1"
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
      )}
      <path
        d={path}
        fill="none"
        stroke="var(--g-accent)"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}
